import React, { useState, useRef, useEffect } from 'react'
import { Square, Circle, Type, Image, Eraser, Grid, Download, Upload } from 'lucide-react'

interface CanvasElement {
  id: string
  type: 'rectangle' | 'circle' | 'text' | 'image'
  x: number
  y: number
  width: number
  height: number
  content?: string
  color: string
  gridPosition: string // e.g., "A1", "B9"
}

interface CollaborativeCanvasProps {
  isActive: boolean
}

const CollaborativeCanvas: React.FC<CollaborativeCanvasProps> = ({ isActive }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [elements, setElements] = useState<CanvasElement[]>([])
  const [selectedTool, setSelectedTool] = useState<'select' | 'rectangle' | 'circle' | 'text' | 'image' | 'eraser'>('select')
  const [currentColor, setCurrentColor] = useState('#3b82f6') // primary blue
  const [gridVisible, setGridVisible] = useState(true)
  const [gridSize, setGridSize] = useState(50) // pixels per grid cell
  const [gridColumns, setGridColumns] = useState(16) // A-P
  const [gridRows, setGridRows] = useState(12) // 1-12
  const [isDrawing, setIsDrawing] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [currentElement, setCurrentElement] = useState<CanvasElement | null>(null)
  const [textInput, setTextInput] = useState('')
  const [showTextInput, setShowTextInput] = useState(false)
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 })

  // Grid coordinate system: A1, B2, etc.
  const gridLabels = Array.from({ length: gridColumns }, (_, i) => 
    String.fromCharCode(65 + i) // A, B, C, ...
  )
  const rowLabels = Array.from({ length: gridRows }, (_, i) => (i + 1).toString())

  const getGridPosition = (x: number, y: number): string => {
    const col = Math.floor(x / gridSize)
    const row = Math.floor(y / gridSize)
    
    if (col >= 0 && col < gridColumns && row >= 0 && row < gridRows) {
      return `${gridLabels[col]}${rowLabels[row]}`
    }
    return 'Outside'
  }

  const getCoordinatesFromGrid = (gridPos: string): { x: number, y: number } | null => {
    const match = gridPos.match(/^([A-Z])(\d+)$/)
    if (!match) return null
    
    const col = gridLabels.indexOf(match[1])
    const row = parseInt(match[2]) - 1
    
    if (col >= 0 && row >= 0 && row < gridRows) {
      return {
        x: col * gridSize + gridSize / 2,
        y: row * gridSize + gridSize / 2
      }
    }
    return null
  }

  const drawGrid = (ctx: CanvasRenderingContext2D) => {
    if (!gridVisible) return
    
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'
    ctx.lineWidth = 1
    
    // Draw vertical lines
    for (let i = 0; i <= gridColumns; i++) {
      ctx.beginPath()
      ctx.moveTo(i * gridSize, 0)
      ctx.lineTo(i * gridSize, canvasRef.current!.height)
      ctx.stroke()
    }
    
    // Draw horizontal lines
    for (let i = 0; i <= gridRows; i++) {
      ctx.beginPath()
      ctx.moveTo(0, i * gridSize)
      ctx.lineTo(canvasRef.current!.width, i * gridSize)
      ctx.stroke()
    }
    
    // Draw grid labels
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.font = '12px monospace'
    
    // Column labels (A, B, C...)
    gridLabels.forEach((label, i) => {
      ctx.fillText(label, i * gridSize + 5, 15)
    })
    
    // Row labels (1, 2, 3...)
    rowLabels.forEach((label, i) => {
      ctx.fillText(label, 5, i * gridSize + 15)
    })
  }

  const drawElements = (ctx: CanvasRenderingContext2D) => {
    elements.forEach(element => {
      ctx.strokeStyle = element.color
      ctx.fillStyle = element.color + '40' // Add transparency
      ctx.lineWidth = 2
      
      switch (element.type) {
        case 'rectangle':
          ctx.strokeRect(element.x, element.y, element.width, element.height)
          ctx.fillRect(element.x, element.y, element.width, element.height)
          break
          
        case 'circle':
          ctx.beginPath()
          ctx.arc(
            element.x + element.width / 2,
            element.y + element.height / 2,
            Math.max(element.width, element.height) / 2,
            0,
            Math.PI * 2
          )
          ctx.stroke()
          ctx.fill()
          break
          
        case 'text':
          ctx.fillStyle = element.color
          ctx.font = '16px Arial'
          ctx.fillText(element.content || '', element.x, element.y)
          break
          
        case 'image':
          // For images, we'd need to handle image loading
          ctx.strokeRect(element.x, element.y, element.width, element.height)
          ctx.fillText('🖼️ Image', element.x + 5, element.y + 20)
          break
      }
      
      // Draw grid position label
      ctx.fillStyle = 'white'
      ctx.font = '10px monospace'
      ctx.fillText(element.gridPosition, element.x + 5, element.y - 5)
    })
  }

  const redrawCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // Clear canvas
    ctx.fillStyle = '#1e293b'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Draw grid
    drawGrid(ctx)
    
    // Draw elements
    drawElements(ctx)
    
    // Draw current element being drawn
    if (isDrawing && currentElement) {
      ctx.strokeStyle = currentElement.color
      ctx.fillStyle = currentElement.color + '40'
      ctx.lineWidth = 2
      
      switch (currentElement.type) {
        case 'rectangle':
          ctx.strokeRect(
            currentElement.x,
            currentElement.y,
            currentElement.width,
            currentElement.height
          )
          ctx.fillRect(
            currentElement.x,
            currentElement.y,
            currentElement.width,
            currentElement.height
          )
          break
          
        case 'circle':
          ctx.beginPath()
          ctx.arc(
            currentElement.x + currentElement.width / 2,
            currentElement.y + currentElement.height / 2,
            Math.max(currentElement.width, currentElement.height) / 2,
            0,
            Math.PI * 2
          )
          ctx.stroke()
          ctx.fill()
          break
      }
    }
  }

  useEffect(() => {
    redrawCanvas()
  }, [elements, gridVisible, isDrawing, currentElement])

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isActive) return
    
    const canvas = canvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const gridPos = getGridPosition(x, y)
    
    setStartPos({ x, y })
    
    if (selectedTool === 'select') {
      // Select element logic would go here
      return
    }
    
    if (selectedTool === 'text') {
      setTextPosition({ x, y })
      setShowTextInput(true)
      return
    }
    
    if (selectedTool === 'image') {
      // Handle image upload/placement
      return
    }
    
    setIsDrawing(true)
    
    const newElement: CanvasElement = {
      id: Date.now().toString(),
      type: selectedTool as 'rectangle' | 'circle',
      x,
      y,
      width: 0,
      height: 0,
      color: currentColor,
      gridPosition: gridPos
    }
    
    setCurrentElement(newElement)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentElement || !canvasRef.current) return
    
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    const updatedElement = {
      ...currentElement,
      width: x - currentElement.x,
      height: y - currentElement.y,
      gridPosition: getGridPosition(currentElement.x, currentElement.y)
    }
    
    setCurrentElement(updatedElement)
    redrawCanvas()
  }

  const handleMouseUp = () => {
    if (!isDrawing || !currentElement) return
    
    if (Math.abs(currentElement.width) > 5 && Math.abs(currentElement.height) > 5) {
      setElements(prev => [...prev, currentElement])
    }
    
    setIsDrawing(false)
    setCurrentElement(null)
  }

  const addTextElement = () => {
    if (!textInput.trim()) {
      setShowTextInput(false)
      return
    }
    
    const newElement: CanvasElement = {
      id: Date.now().toString(),
      type: 'text',
      x: textPosition.x,
      y: textPosition.y,
      width: 0,
      height: 0,
      content: textInput,
      color: currentColor,
      gridPosition: getGridPosition(textPosition.x, textPosition.y)
    }
    
    setElements(prev => [...prev, newElement])
    setTextInput('')
    setShowTextInput(false)
  }

  const clearCanvas = () => {
    setElements([])
  }

  const exportCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const dataURL = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.download = 'collaborative-canvas.png'
    link.href = dataURL
    link.click()
  }

  const tools = [
    { id: 'select' as const, label: 'Select', icon: Square },
    { id: 'rectangle' as const, label: 'Rectangle', icon: Square },
    { id: 'circle' as const, label: 'Circle', icon: Circle },
    { id: 'text' as const, label: 'Text', icon: Type },
    { id: 'image' as const, label: 'Image', icon: Image },
    { id: 'eraser' as const, label: 'Eraser', icon: Eraser },
  ]

  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#64748b'
  ]

  return (
    <div className="h-full flex flex-col bg-secondary-800">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 bg-secondary-700 border-b border-secondary-600">
        <div className="flex items-center space-x-2">
          <h2 className="text-xl font-bold text-primary-400">Collaborative Canvas</h2>
          <div className={`px-2 py-1 rounded text-sm ${
            isActive ? 'bg-accent-500 text-white' : 'bg-secondary-600 text-secondary-400'
          }`}>
            {isActive ? 'Active' : 'Paused'}
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setGridVisible(!gridVisible)}
            className={`flex items-center space-x-2 px-3 py-2 rounded ${
              gridVisible ? 'bg-primary-500 text-white' : 'bg-secondary-600 text-secondary-400'
            }`}
          >
            <Grid className="w-4 h-4" />
            <span>Grid</span>
          </button>
          
          <button
            onClick={clearCanvas}
            className="flex items-center space-x-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
          >
            <Eraser className="w-4 h-4" />
            <span>Clear</span>
          </button>
          
          <button
            onClick={exportCanvas}
            className="flex items-center space-x-2 px-3 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Tools Panel */}
        <div className="w-16 bg-secondary-700 border-r border-secondary-600 flex flex-col items-center py-4 space-y-4">
          {tools.map(tool => (
            <button
              key={tool.id}
              onClick={() => setSelectedTool(tool.id)}
              className={`p-2 rounded transition-all ${
                selectedTool === tool.id
                  ? 'bg-primary-500 text-white'
                  : 'text-secondary-400 hover:text-secondary-200 hover:bg-secondary-600'
              }`}
              title={tool.label}
            >
              <tool.icon className="w-5 h-5" />
            </button>
          ))}
        </div>

        {/* Color Palette */}
        <div className="w-12 bg-secondary-700 border-r border-secondary-600 flex flex-col items-center py-4 space-y-2">
          {colors.map(color => (
            <button
              key={color}
              onClick={() => setCurrentColor(color)}
              className="w-6 h-6 rounded border-2 border-secondary-500 hover:border-white transition-all"
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>

        {/* Canvas Area */}
        <div className="flex-1 relative overflow-auto bg-secondary-900">
          <canvas
            ref={canvasRef}
            width={gridColumns * gridSize}
            height={gridRows * gridSize}
            className="cursor-crosshair"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
          
          {/* Grid Coordinate Display */}
          <div className="absolute top-4 right-4 bg-secondary-800/90 backdrop-blur-sm rounded-lg p-3">
            <div className="text-sm text-secondary-300">
              <div>Grid Size: {gridSize}px</div>
              <div>Columns: {gridColumns} (A-{gridLabels[gridLabels.length - 1]})</div>
              <div>Rows: {gridRows} (1-{gridRows})</div>
              <div className="mt-2 text-accent-400 font-mono">
                Click anywhere to see coordinates
              </div>
            </div>
          </div>
        </div>

        {/* Elements Panel */}
        <div className="w-64 bg-secondary-700 border-l border-secondary-600 p-4">
          <h3 className="text-lg font-semibold text-secondary-300 mb-4">Canvas Elements</h3>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {elements.map(element => (
              <div key={element.id} className="bg-secondary-600 rounded p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-secondary-200 capitalize">
                    {element.type}
                  </span>
                  <span className="text-xs font-mono bg-secondary-500 px-2 py-1 rounded">
                    {element.gridPosition}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-4 h-4 rounded border"
                    style={{ backgroundColor: element.color }}
                  />
                  <span className="text-xs text-secondary-400">
                    {element.content || `${Math.round(element.width)}×${Math.round(element.height)}`}
                  </span>
                </div>
              </div>
            ))}
            
            {elements.length === 0 && (
              <div className="text-center text-secondary-400 py-8">
                <Square className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No elements yet</p>
                <p className="text-sm">Start drawing or add text</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Text Input Modal */}
      {showTextInput && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-secondary-800 rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-secondary-200 mb-4">Add Text</h3>
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter text..."
              className="w-full p-3 bg-secondary-700 rounded border border-secondary-600 text-white mb-4"
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && addTextElement()}
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowTextInput(false)}
                className="px-4 py-2 bg-secondary-600 hover:bg-secondary-500 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={addTextElement}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded"
              >
                Add Text
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div className="bg-secondary-700 border-t border-secondary-600 px-4 py-2">
        <div className="flex justify-between items-center text-sm text-secondary-400">
          <div>
            <span>Tool: </span>
            <span className="text-secondary-200 capitalize">{selectedTool}</span>
            <span className="mx-2">•</span>
            <span>Color: </span>
            <span className="text-secondary-200">{currentColor}</span>
          </div>
          <div>
            <span>Grid: </span>
            <span className="text-secondary-200">
              {gridColumns}×{gridRows} ({gridSize}px cells)
            </span>
            <span className="mx-2">•</span>
            <span>Elements: </span>
            <span className="text-secondary-200">{elements.length}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CollaborativeCanvas
